/**
 * Modale "Nouveau message" (canal messagerie intégrée).
 *
 * - Destinataire prérempli
 * - Sélecteur de modèle
 * - Zone de rédaction enrichie (simulée — barre d'outils basique)
 * - Bouton variables (insertion dans la zone)
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../ui/dropdown-menu';
import {
  X, MessageCircle, Send, Bold, Italic, List, Link2, ChevronDown,
} from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import { useToast } from '../../../hooks/use-toast';
import { buildSuggestedMessage, daysSince } from './shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'inactive', label: 'Relance — Client inactif' },
  { id: 'commercial', label: 'Proposition commerciale' },
  { id: 'after_op', label: 'Suivi après opération' },
  { id: 'feedback', label: 'Demande de feedback' },
];

const VARIABLES = [
  { token: '{{client}}', label: 'Nom client' },
  { token: '{{last_activity}}', label: 'Dernière activité' },
  { token: '{{commercial}}', label: 'Nom commercial' },
  { token: '{{next_slot}}', label: 'Prochaine disponibilité' },
];

export default function MessageModal() {
  const { modal, client, close } = useClientActions();
  const { toast } = useToast();
  const isOpen = modal === 'message' && !!client;

  const [template, setTemplate] = useState('inactive');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (client) setBody(buildSuggestedMessage(client.companyName, daysSince(client.lastSeen)));
  }, [client]);

  if (!isOpen) return null;

  const insertVariable = (token) => {
    setBody((b) => `${b}${b.endsWith(' ') || b.length === 0 ? '' : ' '}${token}`);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/customer-success/actions`, {
        companyName: client.companyName,
        clientEmail: client.primaryEmail,
        action: 'message',
        channel: 'internal_messaging',
        body,
        visibility: 'commercial',
        metadata: { template },
      });
      toast({ title: 'Message envoyé avec succès', description: `${client.companyName} contacté(e).` });
      close();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const recipient = `${client.primaryEmail || '—'} (${client.companyName})`;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden flex flex-col" data-testid="message-modal">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Nouveau message</h2>
              <p className="text-xs text-gray-500">Rédigez votre message à {client.companyName}</p>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="close-message-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pb-4 space-y-4 overflow-auto">
          <div>
            <label className="text-xs text-gray-500 block mb-1">À</label>
            <Input value={recipient} readOnly className="bg-gray-50" data-testid="message-recipient" />
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">Modèle</label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger data-testid="message-template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button className="text-xs text-blue-600 hover:underline mt-5">Personnaliser</button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-500">Message</label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-[11px] text-gray-600 hover:text-gray-900 flex items-center gap-1" data-testid="vars-dropdown">
                    Variables <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {VARIABLES.map((v) => (
                    <DropdownMenuItem key={v.token} onClick={() => insertVariable(v.token)}>
                      <code className="text-[10px] text-emerald-600 mr-2">{v.token}</code> {v.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* Toolbar simulée (visuel uniquement) */}
            <div className="border border-b-0 border-gray-200 rounded-t-md px-2 py-1 flex items-center gap-2 bg-gray-50">
              <ToolbarBtn icon={Bold} />
              <ToolbarBtn icon={Italic} />
              <ToolbarBtn icon={List} />
              <ToolbarBtn icon={Link2} />
            </div>
            <Textarea
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="rounded-t-none font-mono text-xs"
              data-testid="message-body"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={close} data-testid="message-cancel-btn">
            Annuler
          </Button>
          <Button
            size="sm" onClick={handleSend}
            disabled={!body.trim() || sending}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="message-send-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {sending ? 'Envoi...' : 'Envoyer le message'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToolbarBtn({ icon: Icon }) {
  return (
    <button className="p-1 rounded hover:bg-gray-200 text-gray-500" tabIndex={-1}>
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}
